import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import Button from "@mui/material/Button";
import {
  Box,
  Typography,
  Divider,
  Chip,
  MenuItem,
  FormControl,
  Select,
  InputLabel,
} from "@mui/material";
import BadgeIcon from "@mui/icons-material/Badge";
import ContactMailIcon from "@mui/icons-material/ContactMail";
import PersonPinIcon from "@mui/icons-material/PersonPin";
import PolicyIcon from "@mui/icons-material/Policy";
import DescriptionIcon from "@mui/icons-material/Description";
import { toast } from "react-toastify";
import { API } from "../../../api/API";

const ChangePoliceModal = ({ show, handleClose, assure }) => {
  const [polices, setPolices] = useState([]);
  const [currentPolice, setCurrentPolice] = useState(null);
  const [selectedPolice, setSelectedPolice] = useState(null);
  const [contrat, setContrat] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      await Promise.all([getCurrentPolice(), getAvailablePolices()]);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Erreur lors du chargement des données");
    } finally {
      setIsLoading(false);
    }
  };

  const getCurrentPolice = async () => {
    try {
      const { id } = assure;
      const response = await API.get(
        `/contrats-assurances?assure_id=${id}&&statut=true`
      );
      const { data } = response.data;

      if (data?.length > 0) {
        const currentContrat = data[0];
        setContrat(currentContrat);
        setCurrentPolice(currentContrat.police);
      } else {
        setCurrentPolice(null);
      }
    } catch (error) {
      console.error(
        "Erreur lors de la récupération de la police actuelle:",
        error
      );
      //  toast.error("Erreur lors de la récupération de la police actuelle");
      setCurrentPolice(null);
    }
  };

  const getAvailablePolices = async () => {
    try {
      const response = await API.get("/polices");
      const { data } = response.data;
      setPolices(data || []);
    } catch (error) {
      console.error("Erreur lors de la récupération des polices:", error);
      toast.error("Erreur lors de la récupération des polices disponibles");
      setPolices([]);
    }
  };

  const handlePoliceChange = async () => {
    if (!selectedPolice) {
      toast.warn("Veuillez sélectionner une police");
      return;
    }

    try {
      setIsLoading(true);
      await API.put(`/contrats-assurances/${contrat.id}`, {
        ...contrat,
        police_id: selectedPolice.id,
      });

      toast.success("Police mise à jour avec succès");
      handleClose();
      // Vous pourriez vouloir rafraîchir les données parentes ici
    } catch (error) {
      console.error("Erreur lors de la mise à jour de la police:", error);
      toast.error("Erreur lors de la mise à jour de la police");
    } finally {
      setIsLoading(false);
    }
  };
  useEffect(() => {
    if (assure) {
      fetchData();
    }
  }, [assure]);

  if (!assure) return null;

  return (
    <Modal
      size="lg"
      show={show}
      onHide={handleClose}
      aria-labelledby="contained-modal-title-vcenter"
      centered
      backdrop="static"
    >
      <Modal.Header closeButton sx={{ borderBottom: "none", pb: 0 }}>
        <Typography variant="h5" component="h2" sx={{ fontWeight: 600 }}>
          Changer de police
        </Typography>
      </Modal.Header>

      <Modal.Body>
        {/* Section Informations de l'assuré */}
        <Box sx={{ mb: 4, p: 3, backgroundColor: "#f5f5f5", borderRadius: 2 }}>
          <Typography
            variant="h6"
            component="h3"
            sx={{ fontWeight: 600, mb: 2 }}
          >
            Informations de l'assuré
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <BadgeIcon sx={{ mr: 1, color: "primary.main" }} />
              <Typography variant="body1">
                <strong>Nom :</strong> {assure.nom}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <PersonPinIcon sx={{ mr: 1, color: "primary.main" }} />
              <Typography variant="body1">
                <strong>Prénom :</strong> {assure.prenom}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <ContactMailIcon sx={{ mr: 1, color: "primary.main" }} />
              <Typography variant="body1">
                <strong>Matricule :</strong> {assure.matricule}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Section Police actuelle */}
        <Box sx={{ mb: 4, p: 3, backgroundColor: "#f5f5f5", borderRadius: 2 }}>
          <Typography
            variant="h6"
            component="h3"
            sx={{ fontWeight: 600, mb: 2 }}
          >
            Police actuelle
          </Typography>
          {currentPolice ? (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <PolicyIcon sx={{ mr: 1, color: "primary.main" }} />
                <Typography variant="body1">
                  <strong>Police :</strong> {currentPolice.label}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <DescriptionIcon sx={{ mr: 1, color: "primary.main" }} />
                <Typography variant="body1">
                  <strong>Description :</strong> {currentPolice.description}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <BadgeIcon sx={{ mr: 1, color: "primary.main" }} />
                <Typography variant="body1">
                  <strong>Numéro :</strong> {currentPolice.numero}
                </Typography>
              </Box>
            </Box>
          ) : (
            <Typography variant="body1" color="textSecondary">
              Aucune police active trouvée pour cet assuré.
            </Typography>
          )}
        </Box>

        <Divider sx={{ my: 3 }}>
          <Chip label="Changer de police" color="success" />
        </Divider>

        {/* Sélection de la nouvelle police */}
        <Box sx={{ mt: 3 }}>
          <Typography
            variant="h6"
            component="h3"
            sx={{ fontWeight: 600, mb: 2 }}
          >
            Sélectionner une nouvelle police
          </Typography>

          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel id="police-select-label">Police d'assurance</InputLabel>
            <Select
              labelId="police-select-label"
              value={selectedPolice ? JSON.stringify(selectedPolice) : ""}
              onChange={(e) => setSelectedPolice(JSON.parse(e.target.value))}
              label="Police d'assurance"
              disabled={isLoading}
            >
              <MenuItem value="" disabled>
                -- Sélectionnez une police --
              </MenuItem>
              {polices.map((police) => (
                <MenuItem
                  key={police.id}
                  value={JSON.stringify(police)}
                  sx={{ display: "flex", justifyContent: "space-between" }}
                >
                  <Box>
                    <Typography>{police.label}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {police.numero}
                    </Typography>
                  </Box>
                  <Typography variant="caption">
                    {police.description.substring(0, 30)}...
                  </Typography>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
            <Button
              variant="outlined"
              onClick={handleClose}
              disabled={isLoading}
            >
              Annuler
            </Button>
            <Button
              variant="contained"
              onClick={handlePoliceChange}
              disabled={isLoading || !selectedPolice}
              sx={{
                backgroundColor: "#37B049",
                "&:hover": { backgroundColor: "#2e8b3a" },
              }}
            >
              {isLoading ? "Enregistrement..." : "Confirmer le changement"}
            </Button>
          </Box>
        </Box>
      </Modal.Body>
    </Modal>
  );
};

export default ChangePoliceModal;
